/**
 * M10.2: "Fiókom" — account data, e-mail status, password change, active sessions, sign-out.
 * The account is separate from the civilization (shown only as a link-free summary here).
 */
import { InstallHint } from './InstallHint.tsx';
import { useCallback, useEffect, useState } from 'react';
import type { AccountProfile, SessionInfo } from '@galaxis/shared';
import { LegalConsent, LegalFooter, CONSENT_VERSIONS } from './LegalLinks.tsx';
import { ChangePasswordRequestSchema, EmailSchema, UpgradeGuestRequestSchema, ChangeEmailRequestSchema, DELETE_CONFIRM_WORD, ACCEPT_LEGAL_MESSAGE } from '@galaxis/shared';
import { EmblemBadge } from './founding/Emblem.tsx';
import { postOnboarding } from '../api/onboarding.ts';
import { useGameStore } from '../store/game-store.ts';
import { ChangelogLink } from './ChangelogPage.tsx';
import { CLIENT_VERSION } from '../api/updates.ts';
import {
  fetchAccount, fetchSessions, revokeSession, changePassword, setAccountEmail, logout, logoutAll, upgradeGuest, resendVerification,
  changeEmail, downloadAccountData, deleteAccount,
} from '../api/account.ts';

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('hu-HU', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const errText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

function Row({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-4 py-2 border-b border-slate-800 last:border-0">
      <dt className="text-xs text-slate-400 sm:w-44 flex-shrink-0">{label}</dt>
      <dd className="text-sm text-slate-100 min-w-0 break-words" data-testid={testId}>{children}</dd>
    </div>
  );
}

function PwField({ id, label, value, onChange, autoComplete, error }: {
  id: string; label: string; value: string; onChange: (v: string) => void; autoComplete: string; error?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="field-label">{label}</label>
      <input id={id} type="password" className="field-input" value={value} onChange={e => onChange(e.target.value)}
        autoComplete={autoComplete} aria-invalid={error ? 'true' : undefined} />
      {error && <p className="field-error" role="alert">{error}</p>}
    </div>
  );
}

export function AccountView({ notice }: { notice?: string }) {
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([fetchAccount(), fetchSessions()]);
      setProfile(p); setSessions(s.sessions); setLoadError(null);
    } catch (e) { setLoadError(errText(e, 'A fiókadatok betöltése nem sikerült.')); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const signOut = async (all: boolean) => {
    if (profile?.guest && !window.confirm('Vendégként a kijelentkezéssel elveszíted a hozzáférést a civilizációdhoz. Előbb mentsd el a fiókod. Biztosan kilépsz?')) return;
    setBusy(true);
    try { if (all) await logoutAll(); else await logout(); } catch { /* signing out locally anyway */ }
    window.location.replace('/');
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="account-view" style={{ touchAction: 'pan-y' }}>
      <div className="max-w-3xl mx-auto space-y-4">
        <h1 className="text-lg font-semibold text-slate-200">Fiókom</h1>
        {notice && <div className="notice notice-info">{notice}</div>}
        {loadError && <div className="notice notice-error" role="alert">{loadError}</div>}
        <InstallHint />

        <section className="sci-fi-panel rounded-xl p-4" aria-labelledby="acc-data">
          <h2 id="acc-data" className="text-sm font-semibold text-slate-200 mb-2">Fiókadatok</h2>
          {!profile ? <p className="text-sm text-slate-500">Betöltés…</p> : (
            <dl>
              <Row label="Felhasználónév" testId="account-username">{profile.username}</Row>
              <Row label="E-mail-cím" testId="account-email">
                {profile.email ?? <span className="text-slate-500">nincs megadva</span>}{' '}
                {profile.email && (profile.emailVerified
                  ? <span className="ml-1 text-xs px-2 py-0.5 rounded" style={{ color: '#86efac', border: '1px solid rgba(46,204,113,0.4)' }} data-testid="email-verified">Megerősítve</span>
                  : <span className="ml-1 text-xs px-2 py-0.5 rounded" style={{ color: '#fcd34d', border: '1px solid rgba(251,191,36,0.4)' }}>Nincs megerősítve</span>)}
              </Row>
              <Row label="Regisztráció" testId="account-created">{fmt(profile.createdAt)}</Row>
              <Row label="Jelszó utoljára módosítva">{fmt(profile.passwordChangedAt)}</Row>
              <Row label="Civilizáció" testId="account-civ">
                {profile.civilization
                  ? <span className="inline-flex items-center gap-2"><EmblemBadge id={profile.civilization.emblem} color={profile.civilization.color} size={24} />{profile.civilization.name}</span>
                  : <span className="text-slate-500">Még nincs civilizációd a közös galaxisban.</span>}
              </Row>
            </dl>
          )}
        </section>

        {profile?.guest && <GuestUpgradeSection profile={profile} onDone={reload} />}
        {profile && !profile.guest && !profile.emailVerified && <EmailSection onDone={reload} />}
        {profile && !profile.guest && profile.emailVerified && <EmailChangeSection currentEmail={profile.email} />}
        {!profile?.guest && <PasswordSection onDone={reload} />}

        <section className="sci-fi-panel rounded-xl p-4" aria-labelledby="acc-sessions">
          <h2 id="acc-sessions" className="text-sm font-semibold text-slate-200 mb-2">Aktív munkamenetek</h2>
          <ul className="flex flex-col gap-2" data-testid="session-list">
            {sessions.map(s => (
              <li key={s.id} className="rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2" style={{ border: '1px solid rgba(148,163,184,0.2)' }} data-testid="session-item">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100">
                    {s.device}
                    {s.current && <span className="ml-2 text-xs px-2 py-0.5 rounded" style={{ color: '#93c5fd', border: '1px solid rgba(74,158,255,0.4)' }}>Ez az eszköz</span>}
                  </div>
                  <div className="text-xs text-slate-400">Utoljára aktív: {fmt(s.lastUsedAt)} · Belépés: {fmt(s.createdAt)}</div>
                </div>
                {!s.current && (
                  <button type="button" className="btn-secondary" onClick={async () => {
                    try { await revokeSession(s.id); } catch { /* already gone */ }
                    void reload();
                  }}>Kijelentkeztetés</button>
                )}
              </li>
            ))}
          </ul>
        </section>

        {profile && <DataSection guest={profile.guest} hasCivilization={!!profile.civilization} />}

        <section className="sci-fi-panel rounded-xl p-4" aria-labelledby="acc-legal" data-testid="account-legal">
          <h2 id="acc-legal" className="text-sm font-semibold text-slate-200 mb-1">Jogi tájékoztatók</h2>
          {profile?.legal?.accepted
            ? <p className="text-xs text-slate-400 mb-2" data-testid="account-legal-accepted">Elfogadva: {fmt(profile.legal.accepted.at)} (Felhasználási feltételek: {profile.legal.accepted.terms}, Adatkezelési tájékoztató: {profile.legal.accepted.privacy}).</p>
            : profile?.guest ? <p className="text-xs text-slate-400 mb-2">Vendégként a feltételek szerint játszol; a fiók mentésekor kérjük az elfogadásukat.</p> : null}
          <LegalFooter className="!justify-start" />
        </section>

        <section className="flex flex-col sm:flex-row gap-2">
          {profile?.civilization && (
            <button type="button" className="btn-secondary" onClick={() => {
              void postOnboarding('reopen').catch(() => undefined);
              useGameStore.getState().setGuideOpen(true);
              useGameStore.getState().setActiveTab('galaxy');
            }}>Kezdő útmutató megnyitása</button>
          )}
          <button type="button" className="btn-secondary" onClick={() => useGameStore.getState().setFeedbackOpen(true)}>Visszajelzés / hibajelentés</button>
          <ChangelogLink label="Újdonságok / Változásnapló" testId="account-changelog" className="btn-secondary inline-flex items-center justify-center" />
          <span className="text-xs text-slate-500 self-center" data-testid="client-version">Verzió: {CLIENT_VERSION}</span>
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => signOut(false)}>Kijelentkezés</button>
          <button type="button" className="btn-danger" disabled={busy} onClick={() => signOut(true)}>Kijelentkezés minden eszközről</button>
        </section>
      </div>
    </div>
  );
}

function PasswordSection({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ currentPassword: '', password: '', passwordConfirm: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF(x => ({ ...x, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    const parsed = ChangePasswordRequestSchema.safeParse(f);
    const errs: Record<string, string> = {};
    if (!parsed.success) for (const i of parsed.error.errors) { const k = String(i.path[0] ?? 'form'); errs[k] ??= i.message; }
    setErrors(errs);
    if (!parsed.success) return;
    setBusy(true);
    try {
      const r = await changePassword(f.currentPassword, f.password, f.passwordConfirm);
      setMsg({ ok: true, text: r.message });
      setF({ currentPassword: '', password: '', passwordConfirm: '' });
      onDone();
    } catch (err) { setMsg({ ok: false, text: errText(err, 'A jelszó módosítása nem sikerült.') }); } finally { setBusy(false); }
  };

  return (
    <section className="sci-fi-panel rounded-xl p-4" aria-labelledby="acc-pw">
      <h2 id="acc-pw" className="text-sm font-semibold text-slate-200 mb-3">Jelszó módosítása</h2>
      <form onSubmit={submit} className="flex flex-col gap-3 max-w-md" noValidate>
        <PwField id="pw-current" label="Jelenlegi jelszó" value={f.currentPassword} onChange={set('currentPassword')} autoComplete="current-password" error={errors['currentPassword']} />
        <PwField id="pw-new" label="Új jelszó" value={f.password} onChange={set('password')} autoComplete="new-password" error={errors['password']} />
        <PwField id="pw-new2" label="Új jelszó megerősítése" value={f.passwordConfirm} onChange={set('passwordConfirm')} autoComplete="new-password" error={errors['passwordConfirm']} />
        <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Mentés…' : 'Jelszó módosítása'}</button>
        {msg && <div className={`notice ${msg.ok ? 'notice-ok' : 'notice-error'}`} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}
      </form>
    </section>
  );
}

/** Guest play: save the account in place — same civilization, progress and sessions. */
function GuestUpgradeSection({ profile, onDone }: { profile: AccountProfile; onDone: () => void }) {
  const [f, setF] = useState({ username: '', email: '', password: '', passwordConfirm: '' });
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF(s => ({ ...s, [k]: v }));

  if (profile.upgradePending) {
    const resend = async () => {
      try { setMsg({ ok: true, text: (await resendVerification(profile.email!)).message }); } catch (err) { setMsg({ ok: false, text: errText(err, 'Nem sikerült.') }); }
    };
    return (
      <section className="sci-fi-panel rounded-xl p-4" aria-labelledby="acc-guest" data-testid="guest-pending">
        <h2 id="acc-guest" className="text-sm font-semibold text-slate-200 mb-1">Majdnem kész</h2>
        <p className="text-xs text-slate-400 mb-3">Megerősítő levelet küldtünk a(z) {profile.email} címre. A link megnyitása után a fiókod végleges, és bármelyik eszközről beléphetsz. Addig a játék ebben a böngészőben folytatódik.</p>
        <button type="button" className="btn-secondary" style={{ minHeight: 44 }} onClick={() => void resend()}>Levél újraküldése</button>
        {msg && <div className={`notice mt-3 ${msg.ok ? 'notice-ok' : 'notice-error'}`} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}
      </section>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = { ...(f.username.trim() ? { username: f.username.trim() } : {}), email: f.email, password: f.password, passwordConfirm: f.passwordConfirm, acceptLegal: true as const, legalVersions: CONSENT_VERSIONS };
    const parsed = UpgradeGuestRequestSchema.safeParse(body);
    const errs: Record<string, string> = {};
    if (!parsed.success) for (const i of parsed.error.errors) { const k = String(i.path[0] ?? 'form'); errs[k] ??= i.message; }
    if (!accepted) errs['acceptLegal'] = ACCEPT_LEGAL_MESSAGE;
    setErrors(errs);
    if (!parsed.success || !accepted) return;
    setBusy(true); setMsg(null);
    try {
      const r = await upgradeGuest(parsed.data);
      setMsg({ ok: true, text: r.message });
      onDone();
    } catch (err) { setMsg({ ok: false, text: errText(err, 'Nem sikerült.') }); } finally { setBusy(false); }
  };

  return (
    <section className="sci-fi-panel rounded-xl p-4" aria-labelledby="acc-guest" data-testid="guest-upgrade">
      <h2 id="acc-guest" className="text-sm font-semibold text-slate-200 mb-1">Fiók mentése</h2>
      <p className="text-xs text-slate-400 mb-3">Vendégként játszol. Add meg az e-mail-címed és egy jelszót: a civilizációd, a haladásod és az útmutató állása megmarad, és bármelyik eszközről folytathatod.</p>
      <form onSubmit={submit} className="flex flex-col gap-3 max-w-md" noValidate>
        <div>
          <label htmlFor="up-username" className="field-label">Felhasználónév (nem kötelező, most: {profile.username})</label>
          <input id="up-username" className="field-input" value={f.username} onChange={e => set('username')(e.target.value)} autoComplete="username" autoCapitalize="none" spellCheck={false} />
          {errors['username'] && <p className="field-error" role="alert">{errors['username']}</p>}
        </div>
        <div>
          <label htmlFor="up-email" className="field-label">E-mail-cím</label>
          <input id="up-email" type="email" inputMode="email" className="field-input" value={f.email} onChange={e => set('email')(e.target.value)} autoComplete="email" />
          {errors['email'] && <p className="field-error" role="alert">{errors['email']}</p>}
        </div>
        <PwField id="up-password" label="Jelszó" value={f.password} onChange={set('password')} autoComplete="new-password" error={errors['password']} />
        <PwField id="up-password2" label="Jelszó megerősítése" value={f.passwordConfirm} onChange={set('passwordConfirm')} autoComplete="new-password" error={errors['passwordConfirm']} />
        <LegalConsent id="up-legal" checked={accepted} onChange={setAccepted} error={errors['acceptLegal']} />
        <button type="submit" className="btn-primary" style={{ minHeight: 44 }} disabled={busy} data-testid="guest-upgrade-submit">{busy ? 'Mentés…' : 'Fiók mentése'}</button>
        {msg && <div className={`notice ${msg.ok ? 'notice-ok' : 'notice-error'}`} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}
      </form>
    </section>
  );
}

/** Pre-M10.2 accounts (no verified address) can attach one here. */
function EmailSection({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = EmailSchema.safeParse(email);
    if (!parsed.success) { setMsg({ ok: false, text: parsed.error.errors[0]?.message ?? 'Érvénytelen e-mail-cím' }); return; }
    try {
      const r = await setAccountEmail(parsed.data, password);
      setMsg({ ok: true, text: r.message }); setPassword(''); onDone();
    } catch (err) { setMsg({ ok: false, text: errText(err, 'Nem sikerült.') }); }
  };

  return (
    <section className="sci-fi-panel rounded-xl p-4" aria-labelledby="acc-email">
      <h2 id="acc-email" className="text-sm font-semibold text-slate-200 mb-1">E-mail-cím megerősítése</h2>
      <p className="text-xs text-slate-400 mb-3">A fiókodhoz még nem tartozik megerősített e-mail-cím. Add meg, és küldünk egy megerősítő linket.</p>
      <form onSubmit={submit} className="flex flex-col gap-3 max-w-md" noValidate>
        <div>
          <label htmlFor="set-email" className="field-label">E-mail-cím</label>
          <input id="set-email" type="email" inputMode="email" className="field-input" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
        </div>
        <PwField id="set-email-password" label="Jelenlegi jelszó" value={password} onChange={setPassword} autoComplete="current-password" />
        <button type="submit" className="btn-primary">Megerősítő levél küldése</button>
        {msg && <div className={`notice ${msg.ok ? 'notice-ok' : 'notice-error'}`} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}
      </form>
    </section>
  );
}

/** Verified accounts: the new address only takes over after its owner opens the link we mail there. */
function EmailChangeSection({ currentEmail }: { currentEmail: string | null }) {
  const [f, setF] = useState({ newEmail: '', currentPassword: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    const parsed = ChangeEmailRequestSchema.safeParse(f);
    const errs: Record<string, string> = {};
    if (!parsed.success) for (const i of parsed.error.errors) { const k = String(i.path[0] ?? 'form'); errs[k] ??= i.message; }
    setErrors(errs);
    if (!parsed.success) return;
    setBusy(true);
    try {
      const r = await changeEmail(parsed.data.newEmail, parsed.data.currentPassword);
      setMsg({ ok: true, text: r.message });
      setF({ newEmail: '', currentPassword: '' });
    } catch (err) { setMsg({ ok: false, text: errText(err, 'Az e-mail-cím módosítása nem sikerült.') }); } finally { setBusy(false); }
  };

  return (
    <section className="sci-fi-panel rounded-xl p-4" aria-labelledby="acc-email-change" data-testid="email-change">
      <h2 id="acc-email-change" className="text-sm font-semibold text-slate-200 mb-1">E-mail-cím módosítása</h2>
      <p className="text-xs text-slate-400 mb-3">Jelenlegi cím: {currentEmail}. Az új címre megerősítő linket küldünk, és erre a címre is értesítést kapsz; a módosítás a link megnyitásakor lép életbe, ezután minden eszközről kiléptetünk.</p>
      <form onSubmit={submit} className="flex flex-col gap-3 max-w-md" noValidate>
        <div>
          <label htmlFor="ec-new" className="field-label">Új e-mail-cím</label>
          <input id="ec-new" type="email" inputMode="email" className="field-input" value={f.newEmail} onChange={e => setF(x => ({ ...x, newEmail: e.target.value }))} autoComplete="off" />
          {errors['newEmail'] && <p className="field-error" role="alert">{errors['newEmail']}</p>}
        </div>
        <PwField id="ec-password" label="Jelenlegi jelszó" value={f.currentPassword} onChange={v => setF(x => ({ ...x, currentPassword: v }))} autoComplete="current-password" error={errors['currentPassword']} />
        <button type="submit" className="btn-primary" style={{ minHeight: 44 }} disabled={busy}>{busy ? 'Küldés…' : 'Megerősítő levél küldése'}</button>
        {msg && <div className={`notice ${msg.ok ? 'notice-ok' : 'notice-error'}`} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}
      </form>
    </section>
  );
}

/** GDPR: download everything we hold about the account; delete the account (immediate, irreversible). */
function DataSection({ guest, hasCivilization }: { guest: boolean; hasCivilization: boolean }) {
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    setMsg(null);
    try { await downloadAccountData(); setMsg({ ok: true, text: 'Az adataid letöltése elindult.' }); } catch (err) { setMsg({ ok: false, text: errText(err, 'Az export nem sikerült.') }); }
  };

  const remove = async (e: React.FormEvent) => {
    e.preventDefault();
    if (confirm !== DELETE_CONFIRM_WORD) { setMsg({ ok: false, text: `Írd be pontosan: ${DELETE_CONFIRM_WORD}` }); return; }
    setBusy(true); setMsg(null);
    try {
      await deleteAccount(guest ? undefined : password, confirm);
      window.location.replace('/');
    } catch (err) { setMsg({ ok: false, text: errText(err, 'A fiók törlése nem sikerült.') }); setBusy(false); }
  };

  return (
    <section className="sci-fi-panel rounded-xl p-4" aria-labelledby="acc-data-rights" data-testid="data-rights">
      <h2 id="acc-data-rights" className="text-sm font-semibold text-slate-200 mb-1">Adataim és fiók törlése</h2>
      <p className="text-xs text-slate-400 mb-3">Bármikor letöltheted a rólad tárolt adatokat (JSON), vagy véglegesen törölheted a fiókodat.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <button type="button" className="btn-secondary" style={{ minHeight: 44 }} onClick={() => void exportData()} data-testid="export-data">Adataim letöltése (JSON)</button>
        {!open && <button type="button" className="btn-danger" style={{ minHeight: 44 }} onClick={() => setOpen(true)} data-testid="delete-open">Fiók törlése…</button>}
      </div>
      {open && (
        <form onSubmit={remove} className="flex flex-col gap-3 max-w-md mt-4 p-3 rounded-lg" style={{ border: '1px solid rgba(239,68,68,0.4)' }} noValidate data-testid="delete-form">
          <p className="text-sm text-slate-200"><strong>Ez nem vonható vissza.</strong> Azonnal törlődik a neved, e-mail-címed, jelszavad, értesítéseid és visszajelzéseid, és minden eszközről kiléptetünk.{hasCivilization ? ' A civilizációd a galaxisban névtelenül, „Elhagyott civilizáció” néven marad (más játékosok csatái és szerződései hivatkoznak rá), a megosztott csatalinkek megszűnnek.' : ''}</p>
          {!guest && <PwField id="del-password" label="Jelszavad" value={password} onChange={setPassword} autoComplete="current-password" />}
          <div>
            <label htmlFor="del-confirm" className="field-label">A megerősítéshez írd be: {DELETE_CONFIRM_WORD}</label>
            <input id="del-confirm" className="field-input" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false} />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-danger" style={{ minHeight: 44 }} disabled={busy} data-testid="delete-submit">{busy ? 'Törlés…' : 'Fiók végleges törlése'}</button>
            <button type="button" className="btn-secondary" style={{ minHeight: 44 }} onClick={() => { setOpen(false); setPassword(''); setConfirm(''); setMsg(null); }}>Mégsem</button>
          </div>
        </form>
      )}
      {msg && <div className={`notice mt-3 ${msg.ok ? 'notice-ok' : 'notice-error'}`} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}
    </section>
  );
}
